import { useState } from "react";
import { router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import useStorageMigration from "../../hooks/useStorageMigration";
import { pick } from "../../lib/i18n";
import { CurrentUser } from "../../types";

const copy = {
  pl: {
    localDisk: "Lokalny dysk",
    envMissingPre: "Brakuje zmiennych ENV:", and: "i", envMissingMid: ". Dodaj je do pliku", envMissingSuffix: "i zrestartuj serwer.",
    optionalEndpoint: "(opcjonalnie — R2/B2/MinIO)", testing: "Testuję…", testConnection: "Testuj połączenie", connectionOk: "✓ Połączenie OK",
    saving: "Zapisuję…", saveConfig: "Zapisz konfigurację", diskUsage: "Wykorzystanie dysku",
    total: "Razem", free: "Wolne", used: "Zajęte", appFiles: "Pliki app", usedLower: "zajęte",
    fileMigration: "Migracja plików", filesMoved: "plików przeniesiono", errorsLower: "błędów", viewErrorFiles: "Zobacz pliki z błędami →",
  },
  en: {
    localDisk: "Local disk",
    envMissingPre: "Missing ENV variables:", and: "and", envMissingMid: ". Add them to the", envMissingSuffix: "file and restart the server.",
    optionalEndpoint: "(optional — R2/B2/MinIO)", testing: "Testing…", testConnection: "Test connection", connectionOk: "✓ Connection OK",
    saving: "Saving…", saveConfig: "Save configuration", diskUsage: "Disk usage",
    total: "Total", free: "Free", used: "Used", appFiles: "App files", usedLower: "used",
    fileMigration: "File migration", filesMoved: "files moved", errorsLower: "errors", viewErrorFiles: "View files with errors →",
  },
};

type DiskStats = {
  total_bytes: number;
  free_bytes: number;
  used_bytes: number;
  percent_used: number;
  blob_bytes: number;
  path?: string;
  error?: string;
};

type MigrationStatus = {
  in_progress: boolean;
  pending_count: number;
  failed_count: number;
  total: number;
  done: number;
};

type Props = {
  current: { backend: "local" | "s3"; s3_bucket?: string; s3_region?: string; s3_endpoint?: string };
  s3_credentials_present: boolean;
  s3_connection: { ok: boolean; error?: string } | null;
  disk_stats: DiskStats;
  migration: MigrationStatus;
  current_user: CurrentUser;
  products?: { id: number; name: string }[];
  error?: string;
  errors?: string[];
};

function formatBytes(b: number) {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
}

function diskBarColor(pct: number) {
  if (pct >= 90) return "var(--danger)";
  if (pct >= 75) return "oklch(0.72 0.18 80)";
  return "var(--accent)";
}

export default function AdminStorage({ current, s3_credentials_present, s3_connection, disk_stats, migration, current_user, products = [], error, errors }: Props) {
  const t = pick(copy);
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const [backend, setBackend] = useState<"local" | "s3">(current.backend);
  const [s3Bucket, setS3Bucket] = useState(current.s3_bucket ?? "");
  const [s3Region, setS3Region] = useState(current.s3_region ?? "");
  const [s3Endpoint, setS3Endpoint] = useState(current.s3_endpoint ?? "");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(s3_connection);
  const [saving, setSaving] = useState(false);

  const migrationProgress = useStorageMigration({
    done:   migration.done,
    total:  migration.total,
    failed: migration.failed_count,
  });

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch("/admin/storage/test_connection", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
        body: JSON.stringify({ s3_bucket: s3Bucket, s3_region: s3Region, s3_endpoint: s3Endpoint }),
      });
      setTestResult(await res.json());
    } finally {
      setTesting(false);
    }
  };

  const save = () => {
    setSaving(true);
    router.patch("/admin/storage", {
      storage_backend: backend,
      s3_bucket: s3Bucket,
      s3_region: s3Region,
      s3_endpoint: s3Endpoint,
    }, { onFinish: () => setSaving(false) });
  };

  const resumeMigration = () => {
    router.post("/admin/storage/test_connection", {});
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 720 }}>
        <div className="page-hd">
          <div><h2>Storage</h2></div>
        </div>

        {error && (
          <div style={{ marginBottom: 14, padding: "8px 12px", background: "oklch(0.62 0.20 25 / 0.08)", border: "1px solid oklch(0.62 0.20 25 / 0.3)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--danger)" }}>
            {error}
          </div>
        )}

        {errors && errors.length > 0 && (
          <div style={{ marginBottom: 14, padding: "8px 12px", background: "oklch(0.62 0.20 25 / 0.08)", border: "1px solid oklch(0.62 0.20 25 / 0.3)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--danger)" }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-hd"><h3>Backend</h3></div>
          <div className="panel-body">
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              {(["local", "s3"] as const).map((b) => (
                <label key={b} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="radio" name="backend" value={b} checked={backend === b} onChange={() => setBackend(b)} />
                  <span style={{ fontWeight: 500 }}>{b === "local" ? t.localDisk : "Amazon S3 / S3-compatible"}</span>
                </label>
              ))}
            </div>

            {backend === "s3" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {!s3_credentials_present && (
                  <div style={{ padding: "8px 12px", background: "oklch(0.72 0.18 80 / 0.1)", border: "1px solid oklch(0.72 0.18 80 / 0.4)", borderRadius: "var(--radius)", fontSize: 12 }}>
                    {t.envMissingPre} <code>AWS_ACCESS_KEY_ID</code> {t.and} <code>AWS_SECRET_ACCESS_KEY</code>{t.envMissingMid} <code>.env</code> {t.envMissingSuffix}
                  </div>
                )}

                <div className="field">
                  <label>Bucket</label>
                  <input type="text" value={s3Bucket} onChange={(e) => setS3Bucket(e.target.value)} placeholder="my-bucket" />
                </div>
                <div className="field">
                  <label>Region</label>
                  <input type="text" value={s3Region} onChange={(e) => setS3Region(e.target.value)} placeholder="us-east-1" />
                </div>
                <div className="field">
                  <label>Custom endpoint <span style={{ color: "var(--muted)", fontWeight: 400 }}>{t.optionalEndpoint}</span></label>
                  <input type="text" value={s3Endpoint} onChange={(e) => setS3Endpoint(e.target.value)} placeholder="https://…" />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button className="btn btn-secondary btn-sm" onClick={testConnection} disabled={testing || !s3_credentials_present}>
                    {testing ? t.testing : t.testConnection}
                  </button>
                  {testResult && (
                    <span style={{ fontSize: 12, color: testResult.ok ? "var(--accent)" : "var(--danger)" }}>
                      {testResult.ok ? t.connectionOk : `✗ ${testResult.error}`}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? t.saving : t.saveConfig}
              </button>
            </div>
          </div>
        </div>

        {current.backend === "local" && !disk_stats.error && (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.diskUsage}</h3></div>
            <div className="panel-body">
              <div style={{ height: 8, background: "var(--surface-2)", borderRadius: 4, overflow: "hidden", marginBottom: 10 }}>
                <div style={{ height: "100%", width: `${Math.min(disk_stats.percent_used, 100)}%`, background: diskBarColor(disk_stats.percent_used), borderRadius: 4, transition: "width 0.3s" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                {[
                  [t.total, formatBytes(disk_stats.total_bytes)],
                  [t.free, formatBytes(disk_stats.free_bytes)],
                  [t.used, formatBytes(disk_stats.used_bytes)],
                  [t.appFiles, formatBytes(disk_stats.blob_bytes)],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="mono" style={{ fontSize: 10, color: "var(--muted-2)" }}>{label}</div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div className="mono" style={{ fontSize: 10, color: "var(--muted-2)", marginTop: 8 }}>
                {disk_stats.percent_used}% {t.usedLower} · {disk_stats.path}
              </div>
            </div>
          </div>
        )}

        {(migration.pending_count > 0 || migration.failed_count > 0) && (
          <div className="panel">
            <div className="panel-hd"><h3>{t.fileMigration}</h3></div>
            <div className="panel-body">
              <div style={{ height: 6, background: "var(--surface-2)", borderRadius: 3, overflow: "hidden", marginBottom: 10 }}>
                <div style={{ height: "100%", width: migrationProgress.total > 0 ? `${(migrationProgress.done / migrationProgress.total) * 100}%` : "0%", background: "var(--accent)", transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
                {migrationProgress.done} / {migrationProgress.total} {t.filesMoved}
                {migrationProgress.failed > 0 && <span style={{ color: "var(--danger)", marginLeft: 8 }}>· {migrationProgress.failed} {t.errorsLower}</span>}
              </div>
              {migrationProgress.failed > 0 && (
                <a href="/admin/brand_migrations" className="btn btn-ghost btn-sm">
                  {t.viewErrorFiles}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
